import React, { useEffect, useState } from "react";
import { useRepairContext } from "../../contexts/RepairContext";
import { useUserContext } from "../../contexts/UserContext";
import useFormHook from "../../hooks/useForm";
import Button from "../../components/form/Button";
import dot from "dot-object";
import { useMutation } from "@apollo/react-hooks";
import { SET_LINEITEM_ACCEPTED, SET_ESTIMATE_APPROVAL, UPDATE_REPAIR } from "../../graphql/queries";
import { TableHead, TableRow, TableCell } from "../../components/Table";
import Checkbox from "../../components/form/Checkbox";
import { toPrice, padLeft, formatDate } from "../../helpers/functions";
import { getYear } from "date-fns";
import cn from "classnames";

export const ApproveCostEstimateTable = (props) => {
  const { isAdmin } = useUserContext();
  const { user, costEstimate, repairId, repairNumber } = props;
  const [lineItems, setLineItems] = useState(costEstimate.lineItems);
  const { currency, taxRate, createdAt } = costEstimate;
  const year = getYear(new Date(props.createdAt)) - 2000;

  const { formValues, setFormValues, register, unregister, setValue } = useFormHook();

  const RegisterFormProps = { register, unregister, setValue, formValues };

  const { refetchSingle } = useRepairContext();

  const [updateLineItem] = useMutation(SET_LINEITEM_ACCEPTED);
  const [updateApprovalDate] = useMutation(SET_ESTIMATE_APPROVAL);
  const [updateRepair] = useMutation(UPDATE_REPAIR);

  const data = dot.object(formValues);
  const accept = async () => {
    for (let i = 0; i < data.checkboxes.length; i++) {
      await updateLineItem({
        variables: {
          id: lineItems[i].id,
          accepted: data.checkboxes[i].accepted,
        },
      });
    }
    await updateApprovalDate({
      variables: {
        id: costEstimate.id,
        approvedAt: new Date().toISOString(),
      },
    });
    await updateRepair({
      variables: {
        id: repairId,
        set: { status: "accepted" },
      },
    });
    refetchSingle(repairId);
  };
  const reject = async () => {
    await updateRepair({
      variables: {
        id: repairId,
        set: { status: "rejected" },
      },
    });
    refetchSingle(repairId);
  };

  useEffect(() => {
    setLineItems(
      lineItems.map((lineItem) => {
        if (lineItem.articleNumber !== "W13001") return lineItem;
        const sum = lineItems.reduce((acc, cur, index) => {
          if (!data.checkboxes) return acc + cur.price;
          return data.checkboxes[index].accepted && cur.articleNumber !== "W13001" ? acc + cur.price : acc;
        }, 0);
        const discountValue = sum * (costEstimate.discountPercent / 100);
        lineItem.price = discountValue * -1;
        return lineItem;
      })
    );
  }, [data.checkboxes]);

  const Netto = lineItems.reduce((acc, cur, index) => {
    if (!data.checkboxes) return acc + cur.price;
    return data.checkboxes[index].accepted ? acc + cur.price : acc;
  }, 0);

  const taxSum = (Netto / 100) * taxRate;
  const Brutto = Netto + taxSum;
  return (
    <div>
      <div className="flex justify-between py-4 mb-2 border-b border-gray-300 border-solid">
        <h3 className="text-xl font-semibold">Kostenvoranschlag bestätigen</h3>
        {isAdmin && (
          <Button className="print-hide" onClick={() => props.handlePrint && props.handlePrint()}>
            Reparatur drucken
          </Button>
        )}
      </div>
      <p>
        Einzelne Posten können über die Checkbox ausgewählt werden.
        <br />
        Die Gesamtsumme beträgt sich auf alle angenommenen Posten.
      </p>
      <div className="flex justify-end max-w-5xl mx-auto my-4">
        <div>
          <div>
            <span className="font-semibold">Reparaturnummer:</span>
            <div>{`R${year}-${padLeft(repairNumber, 4)}`}</div>
          </div>
          <div>
            <span className="font-semibold">Angebotsnummer:</span>
            <div>{costEstimate.costEstimateNumber + 230000}</div>
          </div>
          <div>
            <span className="font-semibold">Kundennummer:</span>
            <div>{user.customerNumber}</div>
          </div>
          <div>
            <span className="font-semibold">Datum:</span>
            <div>{formatDate(createdAt)}</div>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto my-3">
        <div className="inline-block min-w-full align-middle border-2 border-gray-400 border-l-3 border-b-3">
          <table className="min-w-full">
            <thead>
              <tr>
                <TableHead label="Position" />
                <TableHead label="Angenommen" />
                <TableHead label="Artikelnummer" />
                <TableHead label="Bezeichnung" />
                <TableHead label="Preis" />
              </tr>
            </thead>
            <tbody className="bg-white">
              {lineItems.map((lineItem, index) => {
                return (
                  <TableRow key={lineItem.id}>
                    <TableCell>
                      <div className={cn({ "line-through": !lineItem.accepted })}>{index + 1}</div>
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        id={`checkboxes[${index}].accepted`}
                        {...RegisterFormProps}
                        value={lineItem.accepted}
                        alwaysTrue={lineItem.mandatory}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={cn({ "line-through": false })}>{lineItem.articleNumber}</div>
                    </TableCell>
                    <TableCell>
                      <div className={cn({ "line-through": false })}>
                        <div className="font-medium">{lineItem.headline}</div>
                        {lineItem.description &&
                          lineItem.description.split("\n").map((line, index) => (
                            <React.Fragment key={index}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={cn({ "line-through": false })}>{toPrice(lineItem.price, currency)}</div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between my-4">
          <div className="flex -mx-2">
            <div className="mx-2 text-right">
              <span className="font-semibold">Summe Netto {currency}</span>
              <div>{toPrice(Netto, currency)}</div>
            </div>
            <div className="mx-2 text-right">
              <span className="font-semibold">MwSt {taxRate}%</span>
              <div>{toPrice(taxSum, currency)}</div>
            </div>
            <div className="mx-2 text-right">
              <span className="font-semibold">Summe Brutto {currency}</span>
              <div>{toPrice(Brutto, currency)}</div>
            </div>
          </div>
          <div className="-mx-2">
            <Button onClick={accept} className="mx-2" disabled={props.isTemporary}>
              Ausgewählte Posten annehmen
            </Button>
            <Button onClick={reject} className="mx-2 bg-red-600 hover:bg-red-700" disabled={props.isTemporary}>
              Gesamtes Angebot ablehnen
            </Button>
          </div>
        </div>
        <div className="py-4 mb-2 border-b border-gray-300 border-solid">
          <p className="text-sm ">
            nach Erhalt der Ware sofort zu zahlen
            <br />
            Bitte beachten Sie, dass wir nach Möglichkeit Ersatzteile der ADENSYS GmbH verwenden, <br />
            um wettbewerbsfähige Preise für unsere Reparaturen anbieten zu können.
            <br />
            Sollten Sie ausschließlich OEM-Teile benötigen, teilen Sie uns
            <br /> dies bitte mit, damit wir Ihr Angebot entsprechend anpassen können.
          </p>
        </div>
        <div className="flex justify-between text-xs">
          <div>
            <div>USt.-IdNr.: DE318961218</div>
            <div>St-Nr.: 138/121/22007</div>
            <div>Registergericht / Registry</div>
            <div>Memmingen</div>
            <div>HRB 17846</div>
          </div>
          <div>
            <div>Bankverbindung / Bank account information</div>
            <div>Sparkasse Memmingen</div>
            <div>IBAN: DE87 7315 0000 1001 4899 52</div>
            <div>BIC-SWIFT: BYLADEM1MLM</div>
          </div>
        </div>
      </div>
    </div>
  );
};
